import { useMemo } from "react";

import { t } from "@impero-com/common/utils/i18n";
import { RateGauge } from "@impero-com/ui";
import moment from "moment-timezone";

import {
    ExistingControlResult,
    FutureControlResult,
    DashboardFilters,
} from "api/generated/model";
import { ActivityPieChart } from "components/activityPieChart";
import {
    computeActivitiesCountedByStatus,
    computeCompletionRatePercentage,
    computeOnTimeRatePercentage,
} from "utils/controlResults/activityStatusUtils";

import { useBuildActivitiesUrl } from "../hooks/useBuildActivitiesUrl";
import { useDashboardFilteredData } from "../hooks/useDashboardFilteredData";

import { DashboardCard } from "./dashboardCard/DashboardCard";
import * as theme from "./DashboardCards.css";

interface DashboardCardsProps {
    filters: DashboardFilters;
}

export function DashboardCards({ filters }: DashboardCardsProps) {
    const {
        filteredControlResultsDto,
        filteredFutureControlResultsDto,
        dateRange,
    } = useDashboardFilteredData(filters);

    const buildActivitiesUrl = useBuildActivitiesUrl({
        dateRange,
        filteredControlProgramIds: filters.controlPrograms,
        filteredTags: filters.tags,
        filteredEntityIds: filters.entities,
        showArchivedActivities: filters.showArchivedActivities,
        showInactivePrograms: filters.showInactivePrograms,
    });

    //pie charts data
    const controlResults: ExistingControlResult[] =
        filteredControlResultsDto.content.map((cr) => cr.resource);

    const futureControlResults: FutureControlResult[] =
        filteredFutureControlResultsDto.content.map((cr) => cr.resource);

    const statusCount = useMemo(() => {
        const now = moment();
        return computeActivitiesCountedByStatus(
            controlResults,
            futureControlResults,
            now
        );
    }, [controlResults, futureControlResults]);

    // progress circle percentages
    const completionRatePercentage = useMemo(() => {
        return computeCompletionRatePercentage(statusCount);
    }, [statusCount]);

    const onTimeRatePercentage = useMemo(() => {
        return computeOnTimeRatePercentage(statusCount);
    }, [statusCount]);

    return (
        <div className={theme.flexContainer}>
            {/* Completion rate */}
            <DashboardCard
                title={t("Dashboard_Card_Title_CompletionRate")}
                tooltipText={t("Dashboard_Card_Tooltip_CompletionRate")}
                showTooltip={true}
            >
                <RateGauge
                    percentage={completionRatePercentage}
                    size="large"
                    label={t("Dashboard_Card_Label_CompletionRate")}
                />
            </DashboardCard>
            {/* On time rate */}
            <DashboardCard
                title={t("Dashboard_Card_Title_OnTimeRate")}
                tooltipText={t("Dashboard_Card_Tooltip_OnTimeRate")}
                showTooltip={true}
            >
                <RateGauge
                    percentage={onTimeRatePercentage}
                    size="large"
                    label={t("Dashboard_Card_Label_OnTimeRate")}
                />
            </DashboardCard>
            {/* Activities */}
            <DashboardCard
                title={t("Dashboard_Card_Title_Activities")}
                showTooltip={false}
            >
                <ActivityPieChart
                    numbers={{
                        completed: statusCount.completed,
                        completedAfterDue: statusCount.completedAfterDue,
                        overdue: statusCount.overdue,
                        pending: statusCount.pending,
                        toBeSent: statusCount.toBeSent,
                    }}
                    getActivitiesByStatusUrl={buildActivitiesUrl}
                />
            </DashboardCard>
        </div>
    );
}
